import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { getCurrentPhase, calculateCurrentPhase, calculateMoonPhase } from '../../utils/cycleCalculator'

const CalendarTab = ({ user, userProfile }) => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(null)

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day))
    }
    
    return days
  }

  const getMoonPhaseForDate = (date) => {
    // Calculate moon phase for any given date
    const knownNewMoon = new Date('2024-01-11') // Known new moon
    const daysSinceNewMoon = Math.floor((date - knownNewMoon) / (1000 * 60 * 60 * 24))
    const lunarCycle = 29.5
    const currentLunarDay = (daysSinceNewMoon % lunarCycle) + 1

    if (currentLunarDay >= 1 && currentLunarDay <= 7) {
      return { phase: 'New Moon', emoji: '🌑', energy: 'low' }
    } else if (currentLunarDay >= 8 && currentLunarDay <= 14) {
      return { phase: 'Waxing Moon', emoji: '🌒', energy: 'building' }
    } else if (currentLunarDay >= 15 && currentLunarDay <= 21) {
      return { phase: 'Full Moon', emoji: '🌕', energy: 'high' }
    } else {
      return { phase: 'Waning Moon', emoji: '🌘', energy: 'releasing' }
    }
  }

  const getCyclePhaseForDate = (date) => {
    if (!userProfile?.last_period_date) return null
    
    const lastPeriod = new Date(userProfile.last_period_date)
    const daysSinceLastPeriod = Math.floor((date - lastPeriod) / (1000 * 60 * 60 * 24))
    const cycleLength = userProfile.cycle_length || 28
    
    if (daysSinceLastPeriod >= 0 && daysSinceLastPeriod < cycleLength * 3) {
      const cycleDay = (daysSinceLastPeriod % cycleLength) + 1
      
      if (cycleDay >= 1 && cycleDay <= 5) {
        return { 
          phase: 'Menstrual', 
          emoji: '🩸', 
          color: 'bg-red-100 border-red-300', 
          energy: 'low',
          cycleDay 
        }
      } else if (cycleDay >= 6 && cycleDay <= 13) {
        return { 
          phase: 'Follicular', 
          emoji: '🌱', 
          color: 'bg-green-100 border-green-300', 
          energy: 'building',
          cycleDay 
        }
      } else if (cycleDay >= 14 && cycleDay <= 16) {
        return { 
          phase: 'Ovulatory', 
          emoji: '✨', 
          color: 'bg-yellow-100 border-yellow-300', 
          energy: 'high',
          cycleDay 
        }
      } else {
        return { 
          phase: 'Luteal', 
          emoji: '🍂', 
          color: 'bg-orange-100 border-orange-300', 
          energy: 'focused',
          cycleDay 
        }
      }
    }
    return null
  }

  const getEnergyIndicator = (moonPhase, cyclePhase) => {
    // Combine moon and cycle energy for overall energy level
    const energyLevels = {
      'low': 1,
      'building': 2,
      'focused': 2,
      'releasing': 2,
      'high': 3
    }

    const moonEnergy = energyLevels[moonPhase?.energy] || 2
    const cycleEnergy = cyclePhase ? energyLevels[cyclePhase.energy] : moonEnergy
    
    // Average the energies if both are available
    const averageEnergy = cyclePhase ? Math.round((moonEnergy + cycleEnergy) / 2) : moonEnergy

    if (averageEnergy <= 1.5) {
      return { level: 'Low Energy', color: 'text-blue-600', symbol: '💤', bgColor: 'bg-blue-50' }
    } else if (averageEnergy >= 2.5) {
      return { level: 'High Energy', color: 'text-red-600', symbol: '⚡', bgColor: 'bg-red-50' }
    } else {
      return { level: 'Moderate Energy', color: 'text-green-600', symbol: '🔋', bgColor: 'bg-green-50' }
    }
  }

  const getPhaseInfoForDate = (date) => {
    if (!date) return null
    
    const moonPhase = getMoonPhaseForDate(date)
    // Always check for cycle phase regardless of tracking mode for calendar display
    const cyclePhase = getCyclePhaseForDate(date)
    const energyInfo = getEnergyIndicator(moonPhase, cyclePhase)

    return {
      moon: moonPhase,
      cycle: cyclePhase,
      energy: energyInfo,
      combinedPhase: cyclePhase ? `${cyclePhase.phase} + ${moonPhase.phase}` : moonPhase.phase
    }
  }

  const isToday = (date) => {
    if (!date) return false
    const today = new Date()
    return date.toDateString() === today.toDateString()
  }

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate)
    newDate.setMonth(newDate.getMonth() + direction)
    setCurrentDate(newDate)
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigateMonth(-1)}
          className="p-2 text-gray-600 hover:text-black transition-colors"
        >
          ←
        </button>
        
        <h2 className="italiana-font text-xl text-black">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        
        <button
          onClick={() => navigateMonth(1)}
          className="p-2 text-gray-600 hover:text-black transition-colors"
        >
          →
        </button>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-black mb-3">Calendar Legend</h4>
        <div className="space-y-2">
          {/* Energy Levels */}
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium">Energy Levels:</span>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <span>💤</span>
                <span>Low</span>
              </div>
              <div className="flex items-center space-x-1">
                <span>🔋</span>
                <span>Moderate</span>
              </div>
              <div className="flex items-center space-x-1">
                <span>⚡</span>
                <span>High</span>
              </div>
            </div>
          </div>
          
          {/* Moon Phases */}
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium">Moon:</span>
            <div className="flex items-center space-x-2">
              <span>🌑</span>
              <span>🌒</span>
              <span>🌕</span>
              <span>🌘</span>
            </div>
          </div>

          {/* Cycle Phases */}
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium">Cycle:</span>
            <div className="flex items-center space-x-2">
              <span>🩸</span>
              <span>🌱</span>
              <span>✨</span>
              <span>🍂</span>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 bg-gray-50">
          {dayNames.map(day => (
            <div key={day} className="p-2 text-center text-xs font-medium text-gray-600">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar days */}
        <div className="grid grid-cols-7">
          {getDaysInMonth().map((date, index) => {
            const phaseInfo = getPhaseInfoForDate(date)
            
            return (
              <motion.button
                key={index}
                onClick={() => date && setSelectedDate(date)}
                whileHover={{ scale: date ? 1.02 : 1 }}
                className={`aspect-square p-1 text-xs border-b border-r border-gray-100 relative ${
                  date ? 'hover:bg-gray-50 cursor-pointer' : ''
                } ${isToday(date) ? 'bg-gold bg-opacity-20 border-gold' : ''}
                ${phaseInfo?.cycle?.color || ''}`}
                disabled={!date}
              >
                {date && phaseInfo && (
                  <div className="flex flex-col h-full justify-between">
                    {/* Top row: Date and Energy */}
                    <div className="flex items-center justify-between">
                      <span className={`font-medium text-xs ${isToday(date) ? 'text-gold font-bold' : 'text-gray-900'}`}>
                        {date.getDate()}
                      </span>
                      <span className="text-xs">{phaseInfo.energy.symbol}</span>
                    </div>
                    
                    {/* Bottom row: Phase symbols */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs">{phaseInfo.moon.emoji}</span>
                      {phaseInfo.cycle && (
                        <span className="text-xs">{phaseInfo.cycle.emoji}</span>
                      )}
                    </div>
                    
                    {/* Cycle day indicator */}
                    {phaseInfo.cycle && (
                      <div className="absolute top-0 right-0 w-3 h-3 rounded-full bg-gray-600 text-white text-xs flex items-center justify-center" style={{ fontSize: '8px' }}>
                        {phaseInfo.cycle.cycleDay}
                      </div>
                    )}
                  </div>
                )}
              </motion.button>
            )
          })}
        </div>
      </div>

      {/* Selected Date Info */}
      {selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-50 p-4 rounded-lg"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-black">
              {selectedDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </h3>
            <button
              onClick={() => alert('Add Plan feature coming soon!')}
              className="text-sm gold-accent hover:underline"
            >
              + Add Plan
            </button>
          </div>
          
          {(() => {
            const phaseInfo = getPhaseInfoForDate(selectedDate)
            return phaseInfo && (
              <div className="space-y-3">
                {/* Combined Phase Display */}
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-medium mb-2 text-center">Combined Phase</h4>
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    {phaseInfo.cycle && (
                      <>
                        <span className="text-lg">{phaseInfo.cycle.emoji}</span>
                        <span className="text-sm font-medium">{phaseInfo.cycle.phase}</span>
                        <span className="text-gray-400">+</span>
                      </>
                    )}
                    <span className="text-lg">{phaseInfo.moon.emoji}</span>
                    <span className="text-sm font-medium">{phaseInfo.moon.phase}</span>
                  </div>
                  <p className="text-xs text-center text-gray-600">
                    {phaseInfo.combinedPhase}
                  </p>
                </div>

                {/* Energy Level */}
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${phaseInfo.energy.bgColor} ${phaseInfo.energy.color}`}>
                  <span className="mr-2">{phaseInfo.energy.symbol}</span>
                  {phaseInfo.energy.level}
                </div>

                {/* Phase Information */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Moon Phase */}
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-lg">{phaseInfo.moon.emoji}</span>
                      <span className="text-sm font-medium">{phaseInfo.moon.phase}</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      {phaseInfo.moon.energy === 'low' && 'Perfect for rest, reflection & planning'}
                      {phaseInfo.moon.energy === 'building' && 'Great for starting new projects'}
                      {phaseInfo.moon.energy === 'high' && 'Ideal for presentations & networking'}
                      {phaseInfo.moon.energy === 'releasing' && 'Best for completing & organizing'}
                    </p>
                  </div>

                  {/* Cycle Phase */}
                  {phaseInfo.cycle ? (
                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg">{phaseInfo.cycle.emoji}</span>
                        <span className="text-sm font-medium">{phaseInfo.cycle.phase}</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Day {phaseInfo.cycle.cycleDay} - 
                        {phaseInfo.cycle.energy === 'low' && ' Rest & intuitive work'}
                        {phaseInfo.cycle.energy === 'building' && ' Creative & brainstorming'}
                        {phaseInfo.cycle.energy === 'high' && ' Networking & presentations'}
                        {phaseInfo.cycle.energy === 'focused' && ' Detail work & completion'}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg">📅</span>
                        <span className="text-sm font-medium">No Cycle Data</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Add your cycle info in settings to see menstrual phase guidance
                      </p>
                    </div>
                  )}
                </div>

                {/* Optimal Activities */}
                <div className="bg-white p-3 rounded-lg">
                  <h4 className="text-sm font-medium mb-2">Optimal for Today:</h4>
                  <div className="text-xs space-y-1">
                    {phaseInfo.energy.level === 'High Energy' && (
                      <>
                        <div className="text-green-700">✓ Important meetings & presentations</div>
                        <div className="text-green-700">✓ Networking & social events</div>
                        <div className="text-green-700">✓ Creative launches & big decisions</div>
                      </>
                    )}
                    {phaseInfo.energy.level === 'Low Energy' && (
                      <>
                        <div className="text-blue-700">✓ Planning & strategic thinking</div>
                        <div className="text-blue-700">✓ Rest & self-care</div>
                        <div className="text-blue-700">✓ Reflection & journaling</div>
                      </>
                    )}
                    {phaseInfo.energy.level === 'Moderate Energy' && (
                      <>
                        <div className="text-orange-700">✓ Routine tasks & admin work</div>
                        <div className="text-orange-700">✓ Skill practice & learning</div>
                        <div className="text-orange-700">✓ Organizing & completing projects</div>
                      </>
                    )}
                  </div>
                </div>

                {/* Phase Synergy Insights */}
                {phaseInfo.cycle && (
                  <div className="bg-gold bg-opacity-10 p-3 rounded-lg">
                    <h4 className="text-sm font-medium mb-2">✨ Phase Synergy:</h4>
                    <div className="text-xs text-gray-700">
                      {/* Synergy combinations */}
                      {phaseInfo.cycle.phase === 'Menstrual' && phaseInfo.moon.phase === 'New Moon' && (
                        "🌙🩸 Double rest energy - Perfect for deep visioning and spiritual insights"
                      )}
                      {phaseInfo.cycle.phase === 'Follicular' && phaseInfo.moon.phase === 'Waxing Moon' && (
                        "🌱🌒 Double growth energy - Ideal time to start new projects and initiatives"
                      )}
                      {phaseInfo.cycle.phase === 'Ovulatory' && phaseInfo.moon.phase === 'Full Moon' && (
                        "✨🌕 Double power energy - Maximum visibility and influence - schedule your biggest moves!"
                      )}
                      {phaseInfo.cycle.phase === 'Luteal' && phaseInfo.moon.phase === 'Waning Moon' && (
                        "🍂🌘 Double completion energy - Perfect for finishing projects and organizing"
                      )}
                      {/* Mixed energies */}
                      {(phaseInfo.cycle.phase === 'Menstrual' && phaseInfo.moon.phase !== 'New Moon') && (
                        `Your body wants rest while the moon is ${phaseInfo.moon.energy} - honor your cycle's need for gentleness`
                      )}
                      {(phaseInfo.cycle.phase === 'Ovulatory' && phaseInfo.moon.phase !== 'Full Moon') && (
                        `You have high personal energy while the moon is ${phaseInfo.moon.energy} - great for focused action`
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })()}
        </motion.div>
      )}

      {/* Quick Energy Overview */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-black mb-3">This Week's Energy Flow</h4>
        <div className="grid grid-cols-7 gap-1">
          {(() => {
            const weekStart = new Date()
            weekStart.setDate(weekStart.getDate() - weekStart.getDay())
            
            return Array.from({ length: 7 }, (_, i) => {
              const day = new Date(weekStart)
              day.setDate(weekStart.getDate() + i)
              const phaseInfo = getPhaseInfoForDate(day)
              
              return (
                <div key={i} className="text-center">
                  <div className="text-xs text-gray-600 mb-1">
                    {day.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div className={`w-8 h-8 rounded-full mx-auto flex items-center justify-center text-xs ${
                    phaseInfo?.energy.bgColor || 'bg-gray-100'
                  }`}>
                    {phaseInfo?.energy.symbol || '•'}
                  </div>
                  <div className="flex justify-center space-x-1 mt-1">
                    <span style={{ fontSize: '8px' }}>{phaseInfo?.moon.emoji}</span>
                    {phaseInfo?.cycle && (
                      <span style={{ fontSize: '8px' }}>{phaseInfo?.cycle.emoji}</span>
                    )}
                  </div>
                </div>
              )
            })
          })()}
        </div>
      </div>

      {/* Coming Soon Notice */}
      <div className="bg-blue-50 p-4 rounded-lg text-center">
        <h4 className="font-medium text-blue-900 mb-2">🚀 Coming Soon</h4>
        <p className="text-sm text-blue-800">
          Full calendar integration with event planning, optimal timing recommendations, 
          and automatic energy-based scheduling suggestions.
        </p>
      </div>
    </div>
  )
}

export default CalendarTab